import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { ImageGallery } from "@/pages/admin/ProductParts";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import WarrantyView, { WarrantyBadge, PrintWarranty } from "@/components/WarrantyView";

const blank = (pid = "", order = "", variant = {}, made = "") => ({ product_id: pid, title: "", description: "", images: [], variant, made_date: made || new Date().toISOString().slice(0, 10),
  duration_years: 0, lifetime: true, types: ["Edge stitching"], order_id: order });

function TypePicker({ value, onChange, isAdmin }) {
  const [types, setTypes] = useState([]);
  const [neu, setNeu] = useState("");
  useEffect(() => { api.get("/admin/warranty-types").then((r) => setTypes(r.data)); }, []);
  const add = async () => { try { const r = await api.post("/admin/warranty-types", { name: neu }); setTypes(r.data); onChange([...value, neu.trim()]); setNeu(""); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2" data-testid="warranty-types">
      <div className="flex flex-wrap gap-3">
        {types.map((t) => <label key={t} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={value.includes(t)} onChange={(e) => onChange(e.target.checked ? [...value, t] : value.filter((x) => x !== t))} data-testid={`warranty-type-${t}`} />{t}</label>)}
      </div>
      {isAdmin && <div className="flex gap-2"><input value={neu} onChange={(e) => setNeu(e.target.value)} placeholder="New warranty category" className={inputCls} data-testid="warranty-new-type-input" /><button type="button" disabled={neu.trim().length < 2} onClick={add} className="border border-[#3B220C] px-3 text-sm disabled:opacity-40" data-testid="warranty-add-type-btn">Add</button></div>}
    </div>
  );
}

function WarrantyForm({ initial, onSaved, isAdmin }) {
  const [f, setF] = useState(initial);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/admin/products").then((r) => {
      setProducts(r.data);
      const p = r.data.find((x) => x.product_id === initial.product_id);
      if (p && !initial.serial && !initial.title) {
        setF((cur) => ({ ...cur, title: p.name, images: p.images || [], description: cur.description || p.description || "",
          variant: { ...Object.fromEntries((p.variants || []).map((v) => [v.name, v.options[0]?.label || ""])), ...cur.variant } }));
      }
    });
  }, []); // eslint-disable-line
  const prod = products.find((p) => p.product_id === f.product_id);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickProduct = (id) => {
    const p = products.find((x) => x.product_id === id);
    setF({ ...f, product_id: id, title: f.title || p?.name || "", images: f.images.length ? f.images : p?.images || [], description: f.description || p?.description || "",
      variant: Object.fromEntries((p?.variants || []).map((v) => [v.name, v.options[0]?.label || ""])) });
  };
  const save = async () => {
    try {
      const body = { ...f, order_id: f.order_id || null };
      const r = f.serial ? await api.put(`/admin/warranties/${f.serial}`, body) : await api.post("/admin/warranties", body);
      toast.success(f.serial ? "Warranty updated" : `Warranty created: ${r.data.serial}`); onSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="warranty-form">
      <Field label="Product"><select value={f.product_id} onChange={(e) => pickProduct(e.target.value)} className={inputCls} data-testid="warranty-product-select"><option value="">Choose product...</option>{products.filter((p) => p.type === "physical").map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}</select></Field>
      {prod?.variants?.length > 0 && <div className="grid sm:grid-cols-2 gap-3">{prod.variants.map((v) => (
        <Field key={v.name} label={`Variant \u00b7 ${v.name}`}><select value={f.variant[v.name] || ""} onChange={(e) => setF({ ...f, variant: { ...f.variant, [v.name]: e.target.value } })} className={inputCls} data-testid={`warranty-variant-${v.name}`}>{v.options.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}</select></Field>))}</div>}
      <Field label="Product detail photos (more than one allowed)"><ImageGallery images={f.images} onChange={(images) => setF({ ...f, images })} testid="warranty-gallery" /></Field>
      <Field label="Title"><input value={f.title} onChange={s("title")} className={inputCls} data-testid="warranty-title-input" /></Field>
      <Field label="Description"><textarea rows={3} value={f.description} onChange={s("description")} className={inputCls} data-testid="warranty-desc-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Production date"><input type="date" value={f.made_date} onChange={s("made_date")} className={inputCls} data-testid="warranty-made-date" /></Field>
        <Field label="Warranty length"><select value={f.lifetime ? "life" : f.duration_years} onChange={(e) => setF({ ...f, lifetime: e.target.value === "life", duration_years: e.target.value === "life" ? 0 : Number(e.target.value) })} className={inputCls} data-testid="warranty-duration-select">
          {[1, 2, 3, 4, 5, 10, 15, 20].map((n) => <option key={n} value={n}>{n} year{n > 1 ? "s" : ""}</option>)}<option value="life">Lifetime</option></select></Field>
      </div>
      <Field label="Warranty type"><TypePicker value={f.types} onChange={(types) => setF({ ...f, types })} isAdmin={isAdmin} /></Field>
      <Field label="Buyer's paid order number (optional — shows the warranty in the buyer's account)"><input value={f.order_id || ""} onChange={s("order_id")} placeholder="CE-..." className={inputCls} data-testid="warranty-order-input" /></Field>
      <Btn onClick={save} disabled={!f.product_id || !f.title || !f.types.length} data-testid="warranty-save-btn">{f.serial ? "Save changes" : "Create warranty"}</Btn>
    </div>
  );
}

function Pager({ d, page, setPage }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid="warranty-pager">
      <span className="text-[#6E6259]">{d.total} warranties · page {d.page} of {d.pages}</span>
      <div className="flex gap-2">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-[#E6DFD5] p-2 disabled:opacity-30" data-testid="warranty-prev-page"><ChevronLeft size={14} /></button>
        <button disabled={page >= d.pages} onClick={() => setPage(page + 1)} className="border border-[#E6DFD5] p-2 disabled:opacity-30" data-testid="warranty-next-page"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

export default function Warranty() {
  const { user } = useApp();
  const isAdmin = user.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [flt, setFlt] = useState({ q: "", status: "", type: "" });
  const [page, setPage] = useState(1);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(null);
  const [done, setDone] = useState(null);
  const load = () => api.get("/admin/warranties", { params: { ...flt, page } }).then((r) => setD(r.data));
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [flt, page]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [flt]);
  useEffect(() => {
    api.get("/admin/warranty-types").then((r) => setTypes(r.data));
    if (isAdmin && params.get("order")) {
      let variant = {};
      try { variant = JSON.parse(params.get("variant") || "{}"); } catch (e) { variant = {}; }
      setForm(blank(params.get("product") || "", params.get("order"), variant, params.get("made") || "")); setParams({}, { replace: true });
    } else if (params.get("serial")) {
      api.get("/admin/warranties", { params: { q: params.get("serial") } }).then((r) => { if (r.data.items[0]) setDone(r.data.items[0]); });
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line
  const del = async (w) => { if (!window.confirm(`Delete warranty ${w.serial}?`)) return; try { await api.delete(`/admin/warranties/${w.serial}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6" data-testid="admin-warranty">
      <AdminTitle action={isAdmin && <Btn onClick={() => setForm(blank())} className="flex items-center gap-2" data-testid="warranty-create-btn"><Plus size={14} />Create warranty</Btn>}>Warranty</AdminTitle>
      <div className="flex flex-wrap gap-2">
        <input value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="Search serial, product, title, order, customer..." className={`${inputCls} sm:!w-80`} data-testid="warranty-search" />
        <select value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} className={`${inputCls} !w-40`} data-testid="warranty-status-filter"><option value="">All</option><option value="valid">Active</option><option value="expired">Expired</option></select>
        <select value={flt.type} onChange={(e) => setFlt({ ...flt, type: e.target.value })} className={`${inputCls} !w-48`} data-testid="warranty-type-filter"><option value="">All coverage</option>{types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
      </div>
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {d.items.map((w) => (
          <div key={w.serial} className="p-4 flex flex-wrap items-center gap-4 text-sm" data-testid={`warranty-row-${w.serial}`}>
            <div className="flex-1 min-w-[220px]"><p className="font-mono text-xs text-[#8C4F27]">{w.serial}</p><p className="font-medium">{w.title}</p>
              <p className="text-xs text-[#6E6259]">{w.product_name} · {w.types.join(", ")} · until {w.lifetime ? "Lifetime" : w.expires_at}{w.order_id ? ` \u00b7 ${w.order_id} (${w.customer_name})` : ""}</p></div>
            <WarrantyBadge w={w} />
            <PrintWarranty w={w} className="!px-3 !py-1.5 !text-xs" />
            <button onClick={() => setDone(w)} className="text-xs underline" data-testid={`warranty-view-btn-${w.serial}`}>QR</button>
            <button onClick={() => setForm({ ...w, order_id: w.order_id || "" })} aria-label="Edit" data-testid={`warranty-edit-${w.serial}`}><Pencil size={14} /></button>
            {isAdmin && <button onClick={() => del(w)} aria-label="Delete" className="text-[#9E2A2B]" data-testid={`warranty-delete-${w.serial}`}><Trash2 size={14} /></button>}
          </div>
        ))}
        {!d.items.length && <p className="p-6 text-sm text-[#6E6259]">No warranty cards found.</p>}
      </div>
      <Pager d={d} page={page} setPage={setPage} />
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-3xl font-normal">{form?.serial ? `Edit ${form.serial}` : "Create warranty"}</DialogTitle>
          {form && <WarrantyForm initial={form} isAdmin={isAdmin} onSaved={(w) => { setForm(null); setDone(w); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!done} onOpenChange={(v) => !v && setDone(null)}>
        <DialogContent className="rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="warranty-result-dialog">
          <DialogTitle className="font-serif text-2xl font-normal">Serial number: <span className="font-mono" data-testid="warranty-result-serial">{done?.serial}</span></DialogTitle>
          {done && <WarrantyView w={done} />}
          {done && <Btn onClick={() => { setForm({ ...done, order_id: done.order_id || "" }); setDone(null); }} data-testid="warranty-result-edit-btn">Edit warranty</Btn>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
